import { Interpolation, ThemeProps } from 'styled-components'

import { ColorKeys, Theme, TypographyConfig } from './types'

export const media = {
  tablet: <T extends Theme>({ theme }: { theme: T }): string =>
    `@media (min-width: ${theme.breakpoints.tablet})`,
  pc: <T extends Theme>({ theme }: { theme: T }): string =>
    `@media (min-width: ${theme.breakpoints.pc})`,
}

export const px2vw =
  <T extends Theme>(
    value: number,
    baseSize?: number | keyof T['breakpoints'],
  ) =>
  ({ theme }: { theme: T }): string => {
    const w =
      (typeof baseSize === 'string'
        ? (theme.breakpoints[
            `${baseSize}Value` as keyof Theme['breakpoints']
          ] as number)
        : (baseSize as number)) || theme.breakpoints.tabletValue
    if (!w) throw new Error('Invalid base width provided to px2vw')
    return `${(100 / w) * value}vw`
  }

export const radius =
  <T extends Theme>(r: keyof Theme['radius']) =>
  ({ theme }: { theme: T }): string =>
    `${theme.radius[r]}px`

// NOTE: [Temporary workaround] TypeScript gets slow when using generics to pass down T to ColorKeys<T>
type ColorKeyType = ColorKeys<Theme>

export const color = <T extends Theme>(c: ColorKeyType) => {
  const keys = c.split('.')

  return ({ theme }: { theme: T }): string => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const color = keys.reduce((acc, key) => (acc as any)[key], theme.colors)

    if (typeof color !== 'string') {
      throw new Error(`Unknown color: ${c}`)
    }

    return color
  }
}

export const spacing =
  <T extends Theme>(...args: number[]): ((props: { theme: T }) => string) =>
  ({ theme }: { theme: T }): string =>
    theme.spacing(...args)

export const typography =
  <T extends Theme>(type: keyof T['typography']) =>
  ({ theme }: { theme: T }): Interpolation<ThemeProps<T>> =>
    theme.typography[type as keyof TypographyConfig]
